// `/search?q=` — every match for one query, grouped by kind.
//
// The dropdown in the top bar answers "take me there". This answers "show me
// everything about this", which is the question notes created: a phrase can
// match a dozen passages, and reading them IS the task rather than a step on
// the way somewhere else. So this shows more per kind, shows each note's
// matching passage in full, and does not disappear when you click.

import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import type { SearchHit } from "@crm/shared";
import styles from "./SearchPage.module.css";
import { Card, CardHeader } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { SearchInput } from "../../design/SearchInput.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { useGlobalSearch } from "../../api/queries.js";
import { hitKindName, hitLabel, hitTarget } from "../../components/searchHits.js";

const PAGE_LIMIT = 25;

function HitRow({ hit }: { hit: SearchHit }): ReactNode {
  const target = hitTarget(hit);

  const body = (
    <>
      <span className={styles.title}>{hit.title}</span>
      {hit.subtitle !== null && <span className={styles.subtitle}>{hit.subtitle}</span>}
      {/* Verbatim, and not clamped here: on the page the passage is the thing
          you came to read. */}
      {hit.snippet !== null && <p className={styles.snippet}>{hit.snippet}</p>}
    </>
  );

  // A kind with no screen renders as a plain row rather than a dead link — a
  // result that navigates nowhere teaches somebody the search is broken.
  if (target === null) {
    return (
      <div className={styles.row}>
        {body}
        <span className={styles.noScreen}>{hitKindName(hit.kind)} — no screen yet</span>
      </div>
    );
  }

  return (
    <Link
      className={`${styles.row} ${styles.rowLink}`}
      to={target.to}
      {...(target.search === undefined ? {} : { search: target.search })}
    >
      {body}
    </Link>
  );
}

export function SearchPage(): ReactNode {
  const { q } = useSearch({ from: "/search" });
  const navigate = useNavigate();
  const results = useGlobalSearch(q, PAGE_LIMIT);

  const matched = (results.data?.groups ?? []).reduce((sum, group) => sum + group.hits.length, 0);
  usePageChrome({
    title: "Search",
    ...(q.trim() === "" ? {} : { summary: `${matched} shown` }),
  });

  return (
    <div className={styles.page}>
      <SearchInput
        value={q}
        onCommit={(next) => void navigate({ to: "/search", search: { q: next } })}
        placeholder="Search everything…"
        ariaLabel="Search the CRM"
        className={styles.box}
      />

      {q.trim() === "" ? (
        <Card>
          <CardHeader
            title="Search everything"
            description="Names and numbers are matched as text. Notes are matched by meaning as well, so a question works as well as a keyword."
          />
        </Card>
      ) : (
        <QueryBoundary query={results} label="the search results">
          {(data) => (
            <>
              {/* Stated rather than hidden: an empty answer over a corpus that
                  is still being indexed is not the same as an empty corpus, and
                  a search that reported both the same way would be misleading. */}
              {(data.pending_notes > 0 || !data.semantic) && (
                <p className={styles.caveat}>
                  {!data.semantic && (
                    <>Notes were matched by word only — ranking by meaning is unavailable. </>
                  )}
                  {data.pending_notes > 0 && (
                    <>
                      {data.pending_notes} note{data.pending_notes === 1 ? "" : "s"} still being
                      indexed, and findable by word in the meantime.
                    </>
                  )}
                </p>
              )}

              {data.groups.length === 0 && (
                <Card>
                  <CardHeader title={`Nothing matched “${q.trim()}”`} />
                </Card>
              )}

              {data.groups.map((group) => (
                <Card key={group.kind}>
                  <CardHeader
                    title={hitLabel(group.kind)}
                    description={
                      group.total > group.hits.length
                        ? `${group.hits.length} of ${group.total}`
                        : `${group.total} match${group.total === 1 ? "" : "es"}`
                    }
                  />
                  <div className={styles.rows}>
                    {group.hits.map((hit) => (
                      <HitRow key={`${hit.kind}:${hit.id}`} hit={hit} />
                    ))}
                  </div>
                </Card>
              ))}
            </>
          )}
        </QueryBoundary>
      )}
    </div>
  );
}
